"use client";

import React from "react";
import {
  Grid,
  GridContent,
  GridItem,
  GridLoadMore,
} from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { HighlightCard } from "@/components/HighlightCard/HighlightCard";
import type { HighlightCardElement } from "@/components/HighlightCard/HighlightCard";
import GridCategoryFilter from "@/components/GridCategoryFilter/GridCategoryFilter.client";
import buttonStyles from "@/components/Button/Button.module.scss";
import type { CategoryOverviewPages } from "@/getData/getPostGridData";
import { fetchHighlights } from "@/getData/fetchHighlights";
import { useAnimeKeyWithDep } from "@/hooks/useAnimeKey";
import type { ImageType } from "@/types/baseTypes";
import { mockImage } from "@/ui/mock-data";
import type { HighlightsGridModuleType } from "./HighlightsGrid.server";

interface HighlightsGridClientProps {
  data: HighlightsGridModuleType;
  fallbackImage: ImageType | false | undefined;
  cardHeadlineLevel: number;
  initialPosts: HighlightCardElement[];
  initialTotalPosts: number;
  categories: CategoryOverviewPages[];
  excludePostIds?: number[];
  hidePagination?: boolean;
}

export const mockHighlight: HighlightCardElement = {
  title: "Highlight placeholder",
  subline: "Highlight placeholder subline",
  excerpt:
    "Highlight placeholder excerpt for the loading skeleton state in this card.",
  image: mockImage,
  link: "#",
  date: "01.01.2020",
  end_date: "",
};

const HighlightsGridClient = ({
  data,
  fallbackImage,
  cardHeadlineLevel,
  initialPosts,
  initialTotalPosts,
  categories,
  excludePostIds,
  hidePagination,
}: HighlightsGridClientProps) => {
  const { locale } = useGlobalContext();
  const [activeCategoryId, setActiveCategoryId] = React.useState<number | null>(
    null,
  );
  const [isCategoryChangePending, setIsCategoryChangePending] =
    React.useState(false);
  const { id, posts_per_page } = data.layout;
  const { show_filter } = data.highlights_grid;
  const showCategoryFilter = show_filter && categories.length > 1;
  const loadingItemsCount = posts_per_page || initialPosts.length || 1;
  const scrollTargetSelector = id ? `#${id}` : undefined;
  const refreshKey = useAnimeKeyWithDep({
    ext: "highlights-grid-category-filter",
    deps: [activeCategoryId],
  });

  const handleCategoryChange = (categoryId: number | null) => {
    setIsCategoryChangePending(true);
    setActiveCategoryId(categoryId);
  };

  return (
    <Grid
      postsPerPage={posts_per_page}
      initialItems={initialPosts}
      initialTotalPosts={initialTotalPosts}
      refreshKey={refreshKey}
      fetchItems={async ({ page, postsPerPage, offset, abortController }) => {
        try {
          return await fetchHighlights({
            page,
            postsPerPage,
            offset,
            locale,
            module: data,
            abortController,
            categoryOverride: activeCategoryId ? [activeCategoryId] : undefined,
            excludeIds: excludePostIds,
          });
        } finally {
          setIsCategoryChangePending(false);
        }
      }}
      renderLoading={{ numberOfItemsWhileLoading: loadingItemsCount }}
    >
      {(gridPosts, loading) => {
        const isLoading = loading || isCategoryChangePending;
        const visiblePosts = isLoading
          ? Array(loadingItemsCount).fill(null)
          : gridPosts;

        return (
          <>
            {showCategoryFilter && (
              <GridCategoryFilter
                categories={categories}
                activeCategoryId={activeCategoryId}
                onCategoryChange={handleCategoryChange}
              />
            )}
            <GridContent justifyContent="center" gap="2rem">
              {visiblePosts.map((element, key) => {
                return (
                  <GridItem
                    itemWidth={{
                      mobile: "100%",
                      tablet: "50%",
                    }}
                    key={key}
                  >
                    <HighlightCard
                      skeleton={isLoading}
                      element={
                        isLoading
                          ? mockHighlight
                          : (element as HighlightCardElement)
                      }
                      fallbackImage={fallbackImage}
                      headingLevel={cardHeadlineLevel}
                      type="highlight"
                    />
                  </GridItem>
                );
              })}
            </GridContent>
            {!hidePagination && (
              <GridLoadMore
                grid_navigation="only-load-more"
                buttonStyles={buttonStyles}
                iconPosition="left"
                scrollTargetSelector={scrollTargetSelector}
              />
            )}
          </>
        );
      }}
    </Grid>
  );
};

export default HighlightsGridClient;
